import { Card } from "@/components/ui/card";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from "recharts";
import { TrendingUp, Calendar } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useState } from "react";
// DTC-K: route trend-window anchor through the canonical clock so
// analytics queries are deterministic under `clock.setFixed(...)`.
import { clock } from "@/utils/clock";
import type { Database } from "@/integrations/supabase/types";

type TechnicianPerformanceMetricRow =
  Database["public"]["Tables"]["technician_performance_metrics"]["Row"];

// Only the fields the trend line actually consumes. Selecting these explicitly
// (instead of `*`) keeps the inferred row type small — `.select('*')` on this
// table expands deep enough to trip TS2589 — and avoids overfetching.
type TrendMetricRow = Pick<
  TechnicianPerformanceMetricRow,
  'last_updated' | 'avg_efficiency_book' | 'comeback_rate' | 'diagnostic_accuracy' | 'jobs_completed'
>;

/**
 * Core-100 R4 (CORE-G, CORE-G-INT-004): one trend point. A metric the KPI writer gated as insufficient-sample (NULL)
 * stays NULL, and the chart draws a GAP for it. It used to coerce NULL to zero before scaling, which drew "not enough evidence" as an
 * efficiency of 0% (and an unknown comeback rate as a perfect 0%).
 */
export interface TrendPoint {
  date: string;
  efficiency: number | null;
  comebackRate: number | null;
  diagnosticAccuracy: number | null;
  jobsCompleted: number;
}
const pct = (v: number | null | undefined): number | null =>
  typeof v === 'number' && Number.isFinite(v) ? Math.round(v * 100) : null;
export function toTrendPoint(m: TrendMetricRow): TrendPoint {
  return {
    date: new Date(m.last_updated).toLocaleDateString(),
    efficiency: pct(m.avg_efficiency_book),
    comebackRate: pct(m.comeback_rate),
    diagnosticAccuracy: pct(m.diagnostic_accuracy),
    jobsCompleted: m.jobs_completed || 0,
  };
}

interface TechnicianTrendChartProps {
  technicianProfileId: string;
  shopId: string;
}

type TimeRange = "7d" | "30d" | "90d";

export function TechnicianTrendChart({ technicianProfileId, shopId }: TechnicianTrendChartProps) {
  const [timeRange, setTimeRange] = useState<TimeRange>("30d");

  const { data: trendData, isLoading } = useQuery({
    queryKey: ['technician-trends', technicianProfileId, shopId, timeRange],
    queryFn: async () => {
      const daysBack = timeRange === "7d" ? 7 : timeRange === "30d" ? 30 : 90;
      const startDate = clock.now();
      startDate.setDate(startDate.getDate() - daysBack);

      // KPI-R71-FIX (CRIT-2): scope to the viewing shop. `shopId` was
      // accepted as a prop but never used — a Floo-Network technician with
      // profile-level metrics accumulated across multiple shops would have
      // ALL shops' rows blended into the trend line here, so the chart in
      // shop A's Passport showed numbers polluted with shop B's work.
      // Same KPI, wrong scope → trust break. Also added shopId to the
      // queryKey so React-Query doesn't serve a cached cross-shop result
      // on switch.
      // `.match()` collapses the two id equalities into one filter call: a
      // double `.eq()` on this table's builder tips the chain into TS2589,
      // whereas `.match(...).is(...).gte(...).order(...)` stays shallow.
      const { data: metrics, error } = await supabase
        .from('technician_performance_metrics')
        .select('last_updated, avg_efficiency_book, comeback_rate, diagnostic_accuracy, jobs_completed')
        .match({ technician_profile_id: technicianProfileId, shop_id: shopId })
        .is('category_key', null)
        .gte('last_updated', startDate.toISOString())
        .order('last_updated', { ascending: true })
        .returns<TrendMetricRow[]>();

      if (error) throw error;

      // Transform to chart format
      return metrics?.map(toTrendPoint) || [];
    }
  });

  if (isLoading) {
    return <Card className="p-4"><div className="h-80 animate-pulse bg-muted rounded" /></Card>;
  }

  if (!trendData || trendData.length === 0) {
    return (
      <Card className="p-4">
        <div className="flex items-center gap-2 mb-2">
          <TrendingUp className="h-5 w-5 text-muted-foreground" />
          <h3 className="font-semibold text-foreground">Performance Trends</h3>
        </div>
        <p className="text-sm text-muted-foreground">No trend data available yet</p>
      </Card>
    );
  }

  return (
    <Card className="p-4">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <TrendingUp className="h-5 w-5 text-muted-foreground" />
          <h3 className="font-semibold text-foreground">Performance Trends</h3>
        </div>
        <Select value={timeRange} onValueChange={(v) => setTimeRange(v as TimeRange)}>
          <SelectTrigger className="w-32">
            <Calendar className="h-4 w-4 mr-2" />
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="7d">7 Days</SelectItem>
            <SelectItem value="30d">30 Days</SelectItem>
            <SelectItem value="90d">90 Days</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <ResponsiveContainer width="100%" height={300}>
        <LineChart data={trendData}>
          <CartesianGrid strokeDasharray="3 3" className="stroke-border" />
          <XAxis 
            dataKey="date" 
            className="text-xs text-muted-foreground"
            tick={{ fill: 'hsl(var(--muted-foreground))' }}
          />
          <YAxis 
            className="text-xs text-muted-foreground"
            tick={{ fill: 'hsl(var(--muted-foreground))' }}
          />
          <Tooltip 
            contentStyle={{
              backgroundColor: 'hsl(var(--card))',
              border: '1px solid hsl(var(--border))',
              borderRadius: '0.5rem',
              color: 'hsl(var(--foreground))'
            }}
          />
          <Legend 
            wrapperStyle={{
              color: 'hsl(var(--foreground))'
            }}
          />
          <Line 
            type="monotone" 
            dataKey="efficiency" 
            stroke="hsl(var(--primary))" 
            strokeWidth={2}
            connectNulls={false}
            name="Book-time efficiency % (gap = not enough completed work)"
          />
          <Line 
            type="monotone" 
            dataKey="diagnosticAccuracy" 
            stroke="hsl(var(--success))" 
            strokeWidth={2}
            connectNulls={false}
            name="Diagnostic Accuracy %"
          />
          <Line 
            type="monotone" 
            dataKey="comebackRate" 
            stroke="hsl(var(--destructive))" 
            strokeWidth={2}
            connectNulls={false}
            name="Comeback Rate %"
          />
        </LineChart>
      </ResponsiveContainer>
    </Card>
  );
}
